import ComponentPanel from "./ComponentPanel.js";
import ComponentAnalysisTable from "./ComponentAnalysisTable.js";
import ComponentAnalysisTableGrouped from "./ComponentAnalysisTableGrouped.js";
import ComponentAnalysisTableCategories from "./ComponentAnalysisTableCategories.js";
import ComponentAnalysisChartGrouped from "./ComponentAnalysisChartGrouped.js";
import ComponentAnalysisChartCategories from "./ComponentAnalysisChartCategories";
import ComponentOptionsGrouping from "./ComponentOptionsGrouping";
import ComponentOptionsCategories from "./ComponentOptionsCategories";
import ComponentOptionsCeil from "./ComponentOptionsCeil";
import ComponentOptionsFilter from "./ComponentOptionsFilter";
import ComponentOptionsCurrency from "./ComponentOptionsCurrency.js";
import ComponentOptionsFxView from "./ComponentOptionsFxView.js";
import Analytics from "../Analytics";
import CurrencyService from "../fx/CurrencyService.js";
import * as DomBuilder from "./dom-builder.js";
import * as Utils from "./utils.js";
import * as CsvWriter from "../csv-writer.js";

/**
 *
 * @version 2026-09-04
 * @author Patrik Harag
 */
export default class ComponentPanelAnalysis extends ComponentPanel {

    /** @type {Controller} */
    #controller;

    /** @type {Transaction[]|null} */
    #transactions = null;
    /** @type {Map<string,Filter>|null} */
    #filters = null;

    /** @type {ComponentOptionsGrouping} */
    #componentGroupingOptions;
    /** @type {ComponentOptionsCategories} */
    #componentCategoriesOptions;
    /** @type {ComponentOptionsCeil} */
    #componentCeilOptions;
    /** @type {ComponentOptionsFilter} */
    #componentFilterOptions;
    /** @type {ComponentOptionsCurrency} */
    #componentCurrencyOptions;
    /** @type {ComponentOptionsFxView} */
    #componentFxViewOptions;
    /** @type {HTMLElement} */
    #contentNode = DomBuilder.div();
    /** @type {ComponentAnalysisTable|ComponentAnalysisTableGrouped|ComponentAnalysisTableCategories|null} */
    #tableComponent = null;

    /**
     *
     * @param controller {Controller}
     * @param enableCategories {boolean}
     */
    constructor(controller, enableCategories) {
        super();
        this.#controller = controller;
        this.#componentGroupingOptions = new ComponentOptionsGrouping(() => {
            this.#componentCategoriesOptions.setDisabled(this.#componentGroupingOptions.getGrouping() === null);
            this.refreshTable();
        })
        this.#componentCategoriesOptions = new ComponentOptionsCategories(enableCategories, (selected) => this.refreshTable());
        this.#componentCeilOptions = new ComponentOptionsCeil(() => this.#refreshCeiling());
        this.#componentFilterOptions = new ComponentOptionsFilter(() => this.refreshTable());
        this.#componentCurrencyOptions = new ComponentOptionsCurrency(() => this.refreshTable());
        this.#componentFxViewOptions = new ComponentOptionsFxView(() => this.refreshTable());

        controller.getDataManager().addOnFiltersUpdated(filters => {
            this.#componentFilterOptions.setFilters(filters);
            this.#filters = filters;
            this.refreshTable();
        });
        controller.getDataManager().addOnDatasetsLoaded(datasets => {
            let allTransactions = [];
            // concat
            datasets.forEach(d => {
                allTransactions = allTransactions.concat(d.getTransactions());
            });

            // find min date and max date and adjust time filter
            if (allTransactions.length > 0) {
                let firstTransaction = allTransactions.reduce((a, b) => a.getDate() < b.getDate() ? a : b);
                let lastTransaction = allTransactions.reduce((a, b) => a.getDate() > b.getDate() ? a : b);
                this.#componentFilterOptions.setTimeSpan(firstTransaction.getDate(), lastTransaction.getDate());
            } else {
                this.#componentFilterOptions.setTimeSpan(null, null);
            }

            // refresh table
            this.#transactions = allTransactions;
            this.refreshTable();
        });
        controller.getDataManager().addOnFxRatesUpdated(() => {
            this.refreshTable();
        });
    }

    getTabId() {
        return 'finance-tab-analysis';
    }

    getTabName() {
        return 'Exploration';
    }

    createNode() {
        return DomBuilder.div({ class: 'panel-analysis' }, [
            DomBuilder.div({ class: 'options-bar' }, [
                this.#componentGroupingOptions.createNode(),
                this.#componentCategoriesOptions.createNode(),
                this.#componentCeilOptions.createNode(),
                this.#componentFilterOptions.createNode(),
                this.#componentCurrencyOptions.createNode(),
                this.#componentFxViewOptions.createNode()
            ]),
            this.#contentNode
        ]);
    }

    #downloadCsv() {
        if (this.#tableComponent === null) {
            return;
        }
        CsvWriter.downloadCsv('analysis.csv', this.#tableComponent.toCsv());
    }

    refreshTable() {
        this.#refreshCeiling();
        this.#contentNode.replaceChildren();
        this.#tableComponent = null;

        if (this.#transactions !== null && this.#filters !== null) {
            let grouping = this.#componentGroupingOptions.getGrouping();
            let selectedFilter = this.#componentFilterOptions.getCombinedFilter();
            let allFilters = this.#componentFilterOptions.getAllFilters();

            let filteredTransactions = this.#transactions.filter(t => selectedFilter.matches(t));
            let targetCurrency = this.#componentCurrencyOptions.getCurrency();
            let currencies = CurrencyService.collectCurrencies(filteredTransactions);
            let needsConversion = false;
            currencies.forEach(c => {
                if (c !== targetCurrency) {
                    needsConversion = true;
                }
            });

            this.#componentFxViewOptions.setDisabled(currencies.size <= 1);
            let split = this.#componentFxViewOptions.isSelected() && currencies.size > 1;

            if (split) {
                for (let currency of CurrencyService.orderCurrencies(currencies)) {
                    let nativeTransactions = filteredTransactions.filter(t => t.getCurrency() === currency);
                    this.#contentNode.append(DomBuilder.element('h2', { class: 'fx-section-title' }, currency));
                    this.#appendAnalysisView(nativeTransactions, grouping, allFilters, selectedFilter, false);
                }

                let convertedTransactions = CurrencyService.convertTransactions(filteredTransactions, targetCurrency);
                this.#contentNode.append(DomBuilder.element('h2', { class: 'fx-section-title' }, targetCurrency + ' (converted)'));
                let groupedForCharts = this.#appendAnalysisView(convertedTransactions, grouping, allFilters, selectedFilter, true);
                this.#appendCharts(groupedForCharts, allFilters, selectedFilter);
            } else {
                let viewTransactions = needsConversion
                        ? CurrencyService.convertTransactions(filteredTransactions, targetCurrency)
                        : filteredTransactions;
                let groupedForCharts = this.#appendAnalysisView(viewTransactions, grouping, allFilters, selectedFilter, true);
                this.#appendCharts(groupedForCharts, allFilters, selectedFilter);
            }

            Analytics.triggerFeatureUsed(Analytics.FEATURE_APP_INITIALIZED);
        }
    }

    /**
     *
     * @param transactions {Transaction[]}
     * @param grouping {Grouping|null}
     * @param allFilters {Map<string,Filter>}
     * @param selectedFilter {Filter}
     * @param forCsv {boolean}
     * @return {Map<string,Transaction[]>|null}
     */
    #appendAnalysisView(transactions, grouping, allFilters, selectedFilter, forCsv) {
        if (grouping !== null) {
            let groupedTransactions = grouping.createGroups(transactions);

            let showCategories = this.#componentCategoriesOptions.isSelected();
            let tableComponent = (showCategories)
                    ? new ComponentAnalysisTableCategories(this.#controller, groupedTransactions, allFilters, selectedFilter)
                    : new ComponentAnalysisTableGrouped(this.#controller, groupedTransactions, allFilters, selectedFilter);
            if (forCsv) {
                this.#tableComponent = tableComponent;
            }
            this.#contentNode.append(tableComponent.createNode());

            this.#contentNode.append(this.#createSummary(transactions.length, groupedTransactions.size, this.#groupValues(groupedTransactions), forCsv, transactions));
            return groupedTransactions;
        }

        let tableComponent = new ComponentAnalysisTable(this.#controller, transactions, allFilters, selectedFilter);
        if (forCsv) {
            this.#tableComponent = tableComponent;
        }
        this.#contentNode.append(tableComponent.createNode());

        this.#contentNode.append(this.#createSummary(transactions.length, null, transactions.map(t => t.getValue()), forCsv, transactions));
        return null;
    }

    /**
     *
     * @param groupedTransactions {Map<string,Transaction[]>|null}
     * @param allFilters {Map<string,Filter>}
     * @param selectedFilter {Filter}
     */
    #appendCharts(groupedTransactions, allFilters, selectedFilter) {
        if (groupedTransactions === null) {
            return;
        }
        if (this.#componentCategoriesOptions.isSelected()) {
            let chartComponent = new ComponentAnalysisChartCategories(this.#controller, groupedTransactions, allFilters, selectedFilter);
            this.#contentNode.append(chartComponent.createNode());
            chartComponent.refresh();
        }

        let chartComponent = new ComponentAnalysisChartGrouped(this.#controller, groupedTransactions, allFilters, selectedFilter);
        this.#contentNode.append(chartComponent.createNode());
        chartComponent.refresh();
    }

    /**
     *
     * @param groupedTransactions {Map<string,Transaction[]>}
     * @return {number[]}
     */
    #groupValues(groupedTransactions) {
        let values = [];
        for (let transactions of groupedTransactions.values()) {
            let result = 0;
            for (let t of transactions) {
                result += t.getValue();
            }
            values.push(result);
        }
        return values;
    }

    /**
     *
     * @param transactionCount {number}
     * @param groupCount {number|null}
     * @param values {number[]}
     * @param showDownload {boolean}
     * @param transactions {Transaction[]}
     * @return {HTMLElement}
     */
    #createSummary(transactionCount, groupCount, values, showDownload, transactions) {
        let text = "transactions: " + transactionCount + "; "
        if (groupCount !== null) {
            text += "groups: " + groupCount + " [";
        }
        let content = [text];
        if (values.length > 0) {
            let min = values[0];
            let max = min;
            let sum = 0;
            for (let v of values) {
                if (v < min) {
                    min = v;
                }
                if (v > max) {
                    max = v;
                }
                sum += v;
            }
            content.push("min: ", ...Utils.createFxValue(min, transactions),
                    "; max: ", ...Utils.createFxValue(max, transactions),
                    "; avg: ", ...Utils.createFxValue(sum / values.length, transactions));

            if (groupCount !== null) {
                content.push("]")
            }
        }
        if (showDownload) {
            content.push(" ", DomBuilder.link('[download]', null, () => this.#downloadCsv()));
        }
        return DomBuilder.div({ class: 'summary-panel' }, content);
    }

    /**
     * @param text {string}
     * @return {HTMLElement}
     */
    #createFxNote(text) {
        return DomBuilder.div({ class: 'fx-note' }, text);
    }

    #refreshCeiling() {
        let selected = this.#componentCeilOptions.isSelected();
        if (selected) {
            this.#contentNode.classList.remove('hide-value-floating-part');
        } else {
            this.#contentNode.classList.add('hide-value-floating-part');
        }
    }
}
